import { motion } from 'framer-motion'
import { Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useTeamMembers } from '@/hooks/useTeamMembers'
import { TeamMember } from './TeamMember'
import { Loader2 } from 'lucide-react'
import { fadeInUp, viewportConfig, getWillChangeStyle, getDuration } from '@/lib/animations/optimized-animations'

export function TeamSection() {
  const { t } = useTranslation('home')
  const { teamMembers, loading, error } = useTeamMembers()

  return (
    <section id="team" className="relative py-24 overflow-hidden">
      {/* No section background: the global animated NeuralBackground (glow +
          pulsing neural network) shows through, keeping the whole page on one
          consistent backdrop like the deck. */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          {...fadeInUp}
          viewport={viewportConfig}
          transition={{ duration: getDuration(0.6) }}
          className="text-center mb-16"
          style={getWillChangeStyle(['opacity', 'transform'])}
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <Users className="w-10 h-10 text-blue-600 dark:text-orange-400" />
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white">
              {t('team.title')}
            </h2>
          </div>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            {t('team.description')}
          </p>
        </motion.div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-blue-600 dark:text-orange-400 animate-spin" />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-12">
            <p className="text-red-600 dark:text-red-400">{t('team.loadingError')}</p>
          </div>
        )}

        {/* Team Grid */}
        {!loading && !error && teamMembers.length > 0 && (
          <div className="flex flex-wrap justify-center gap-8">
            {teamMembers.map((member, index) => (
              <div key={member.id} className="w-full sm:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.3rem)] xl:w-[calc(25%-1.5rem)] min-w-70 max-w-87.5">
                <TeamMember member={member} index={index} />
              </div>
            ))}
          </div>
        )}

        {/* CTA Section */}
        <motion.div
          {...fadeInUp}
          viewport={viewportConfig}
          transition={{ duration: getDuration(0.6), delay: getDuration(0.3) }}
          className="mt-16 text-center"
          style={getWillChangeStyle(['opacity', 'transform'])}
        >
          <div className="cta-gradient-card rounded-2xl p-12 shadow-2xl">
            <h3 className="cta-title text-3xl font-bold mb-4">
              {t('team.cta.title')}
            </h3>
            <p className="cta-description text-white/90 text-lg mb-8 max-w-2xl mx-auto">
              {t('team.cta.description')}
            </p>
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="cta-button px-8 py-4 rounded-xl font-semibold text-lg shadow-lg inline-block cursor-pointer"
            >
              {t('team.cta.button')}
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
